import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
    Truck, 
    Plus, 
    Pencil, 
    Save,
    Loader2,
    Clock,
    Euro,
    MapPin,
    RefreshCw,
    Check,
    X,
    Package,
    Home,
    Building2,
    AlertCircle,
    Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminShipping = () => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Boxtal offers from API
    const [boxtalOffers, setBoxtalOffers] = useState([]);
    const [boxtalError, setBoxtalError] = useState(null);
    
    // Configured carriers (from settings)
    const [carriers, setCarriers] = useState([]);
    
    // Sender address
    const [senderAddress, setSenderAddress] = useState({
        street: '',
        postal_code: '',
        city: '',
        country: 'FR'
    });
    const [defaultWeight, setDefaultWeight] = useState(0.2);
    
    // Modal state for editing a carrier
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCarrier, setEditingCarrier] = useState(null);
    const [carrierForm, setCarrierForm] = useState({
        code: '',
        name: '',
        price: 0,
        delay: '',
        cutoff: '17h',
        franco: 500,
        saturday_delivery: false,
        is_relay: false,
        is_pickup: false,
        enabled: true
    });

    // Manual carrier modal
    const [manualModalOpen, setManualModalOpen] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/shipping-settings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = response.data;
            
            if (data.carriers && data.carriers.length > 0) {
                setCarriers(data.carriers);
            }
            
            if (data.sender_address) {
                setSenderAddress(data.sender_address);
            }
            if (data.default_weight_kg) {
                setDefaultWeight(data.default_weight_kg);
            }
            
            // Fetch Boxtal offers
            await fetchBoxtalOffers();
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Erreur lors du chargement des paramètres');
        } finally {
            setLoading(false);
        }
    };

    const fetchBoxtalOffers = async () => {
        setLoadingOffers(true);
        setBoxtalError(null);
        try {
            const response = await axios.get(`${API_URL}/admin/boxtal-offers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBoxtalOffers(response.data.offers || []);
        } catch (error) {
            console.error('Error fetching Boxtal offers:', error);
            setBoxtalError(error.response?.data?.detail || 'Erreur de connexion à Boxtal');
        } finally {
            setLoadingOffers(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API_URL}/admin/shipping-settings`,
                {
                    carriers: carriers,
                    sender_address: senderAddress,
                    default_weight_kg: defaultWeight
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Paramètres de transport enregistrés');
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // Toggle a Boxtal offer on/off
    const toggleBoxtalOffer = (offer) => {
        const code = offer.full_code;
        const existingIdx = carriers.findIndex(c => c.code === code);
        
        if (existingIdx >= 0) {
            // Toggle existing
            setCarriers(prev => prev.map((c, i) => 
                i === existingIdx ? { ...c, enabled: !c.enabled } : c
            ));
        } else {
            // Add new carrier configuration from Boxtal offer
            const newCarrier = {
                code: code,
                name: `${offer.carrier_name} - ${offer.service_label || offer.carrier_code}`,
                price: offer.boxtal_price || 0,
                delay: '',
                cutoff: '17h',
                franco: 500,
                saturday_delivery: false,
                is_relay: offer.is_relay || false,
                is_pickup: false,
                is_home_delivery: offer.is_home_delivery || false,
                carrier_logo: offer.carrier_logo,
                enabled: true
            };
            setCarriers(prev => [...prev, newCarrier]);
        }
    };

    // Check if an offer is enabled
    const isOfferEnabled = (offer) => {
        const carrier = carriers.find(c => c.code === offer.full_code);
        return carrier?.enabled || false;
    };

    // Get carrier config for an offer
    const getCarrierConfig = (offer) => {
        return carriers.find(c => c.code === offer.full_code);
    };

    // Open edit modal for a Boxtal offer
    const openEditModal = (offer) => {
        const config = getCarrierConfig(offer) || {
            code: offer.full_code,
            name: `${offer.carrier_name} - ${offer.service_label || offer.carrier_code}`,
            price: offer.boxtal_price || 0,
            delay: '',
            cutoff: '17h',
            franco: 500,
            saturday_delivery: false,
            is_relay: offer.is_relay || false,
            is_pickup: false,
            enabled: true
        };
        
        setEditingCarrier(offer.full_code);
        setCarrierForm({
            ...config,
            boxtal_price: offer.boxtal_price,
            carrier_logo: offer.carrier_logo
        });
        setModalOpen(true);
    };

    // Save carrier config from modal
    const saveCarrierConfig = () => {
        const existingIdx = carriers.findIndex(c => c.code === editingCarrier);
        
        if (existingIdx >= 0) {
            setCarriers(prev => prev.map((c, i) => 
                i === existingIdx ? { ...carrierForm } : c
            ));
        } else {
            setCarriers(prev => [...prev, { ...carrierForm }]);
        }
        
        setModalOpen(false);
        toast.success('Configuration mise à jour');
    };

    // Open manual carrier modal
    const openManualModal = () => {
        setCarrierForm({
            code: '',
            name: '',
            price: 0,
            delay: 'J+1',
            cutoff: '17h',
            franco: 500,
            saturday_delivery: false,
            is_relay: false,
            is_pickup: false,
            enabled: true
        });
        setManualModalOpen(true);
    };

    // Save manual carrier
    const saveManualCarrier = () => {
        if (!carrierForm.code || !carrierForm.name) {
            toast.error('Le code et le nom sont requis');
            return;
        }
        
        // Check for duplicate
        if (carriers.find(c => c.code === carrierForm.code)) {
            toast.error('Ce code transporteur existe déjà');
            return;
        }
        
        setCarriers(prev => [...prev, { ...carrierForm }]);
        setManualModalOpen(false);
        toast.success('Transporteur manuel ajouté');
    };

    // Get enabled carriers count
    const enabledCount = carriers.filter(c => c.enabled).length;

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center h-96">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8 space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                            Configuration Transport
                        </h1>
                        <p className="text-slate-600 mt-1">
                            Gérez vos transporteurs Boxtal et tarifs personnalisés
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={fetchBoxtalOffers}
                            disabled={loadingOffers}
                        >
                            {loadingOffers ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Actualiser Boxtal
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Enregistrer
                        </Button>
                    </div>
                </div>

                {/* Status Badge */}
                <div className="flex items-center gap-4">
                    <Badge variant="outline" className="text-sm px-3 py-1">
                        <Check className="w-4 h-4 mr-1 text-green-600" />
                        {enabledCount} transporteur{enabledCount > 1 ? 's' : ''} actif{enabledCount > 1 ? 's' : ''}
                    </Badge>
                    {boxtalOffers.length > 0 && (
                        <Badge variant="secondary" className="text-sm px-3 py-1">
                            <Zap className="w-4 h-4 mr-1 text-yellow-600" />
                            {boxtalOffers.length} offres Boxtal disponibles
                        </Badge>
                    )}
                </div>

                {/* Sender Address Card */}
                <Card className="border-slate-200">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <MapPin className="w-5 h-5 text-blue-600" />
                            Adresse d'expédition
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="md:col-span-2">
                                <Label>Adresse</Label>
                                <Input
                                    value={senderAddress.street || ''}
                                    onChange={(e) => setSenderAddress(prev => ({ ...prev, street: e.target.value }))}
                                    placeholder="123 Rue de l'Entreprise"
                                />
                            </div>
                            <div>
                                <Label>Code postal</Label>
                                <Input
                                    value={senderAddress.postal_code || ''}
                                    onChange={(e) => setSenderAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                                    placeholder="75001"
                                />
                            </div>
                            <div>
                                <Label>Ville</Label>
                                <Input
                                    value={senderAddress.city || ''}
                                    onChange={(e) => setSenderAddress(prev => ({ ...prev, city: e.target.value }))}
                                    placeholder="Paris"
                                />
                            </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                            Cette adresse est utilisée pour calculer les tarifs d'expédition via Boxtal
                        </p>
                    </CardContent>
                </Card>

                {/* Main Content Tabs */}
                <Tabs defaultValue="boxtal" className="w-full">
                    <TabsList className="grid w-full max-w-md grid-cols-2">
                        <TabsTrigger value="boxtal" className="flex items-center gap-2">
                            <Zap className="w-4 h-4" />
                            Offres Boxtal
                        </TabsTrigger>
                        <TabsTrigger value="manual" className="flex items-center gap-2">
                            <Truck className="w-4 h-4" />
                            Transporteurs manuels
                        </TabsTrigger>
                    </TabsList>

                    {/* Boxtal Offers Tab */}
                    <TabsContent value="boxtal" className="mt-6">
                        {boxtalError ? (
                            <Card className="border-red-200 bg-red-50">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-3 text-red-700">
                                        <AlertCircle className="w-5 h-5" />
                                        <div>
                                            <p className="font-medium">Erreur de connexion Boxtal</p>
                                            <p className="text-sm">{boxtalError}</p>
                                        </div>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        className="mt-4"
                                        onClick={fetchBoxtalOffers}
                                    >
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Réessayer
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : loadingOffers ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                                <span className="ml-3 text-slate-600">Chargement des offres Boxtal...</span>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-600 mb-4">
                                    Activez les offres de transport que vous souhaitez proposer à vos clients. 
                                    Les prix affichés sont indicatifs et peuvent varier selon la destination.
                                </p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {boxtalOffers.map((offer) => {
                                        const isEnabled = isOfferEnabled(offer);
                                        const config = getCarrierConfig(offer);
                                        
                                        return (
                                            <Card 
                                                key={offer.full_code}
                                                className={`border-2 transition-all cursor-pointer ${
                                                    isEnabled 
                                                        ? 'border-blue-300 bg-blue-50/50' 
                                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                                }`}
                                                onClick={() => toggleBoxtalOffer(offer)}
                                            >
                                                <CardContent className="p-4">
                                                    {/* Header */}
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div className="flex items-center gap-3">
                                                            {offer.carrier_logo ? (
                                                                <img 
                                                                    src={offer.carrier_logo} 
                                                                    alt={offer.carrier_name}
                                                                    className="h-8 w-auto object-contain"
                                                                    onError={(e) => e.target.style.display = 'none'}
                                                                />
                                                            ) : (
                                                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                                    <Truck className="w-5 h-5 text-slate-400" />
                                                                </div>
                                                            )}
                                                            <div>
                                                                <h3 className="font-semibold text-slate-900 text-sm">
                                                                    {offer.carrier_name}
                                                                </h3>
                                                                <p className="text-xs text-slate-500">
                                                                    {offer.service_label || offer.service_code}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div 
                                                            className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                                isEnabled ? 'bg-blue-600' : 'bg-slate-200'
                                                            }`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleBoxtalOffer(offer);
                                                            }}
                                                        >
                                                            {isEnabled && <Check className="w-4 h-4 text-white" />}
                                                        </div>
                                                    </div>

                                                    {/* Price & Type */}
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            {offer.is_relay ? (
                                                                <Badge variant="outline" className="text-xs">
                                                                    <Package className="w-3 h-3 mr-1" />
                                                                    Relais
                                                                </Badge>
                                                            ) : offer.is_home_delivery ? (
                                                                <Badge variant="outline" className="text-xs">
                                                                    <Home className="w-3 h-3 mr-1" />
                                                                    Domicile
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="outline" className="text-xs">
                                                                    <Building2 className="w-3 h-3 mr-1" />
                                                                    Entreprise
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-lg font-bold text-blue-600">
                                                                {(config?.price || offer.boxtal_price || 0).toFixed(2)}€
                                                            </p>
                                                            {config?.price && config.price !== offer.boxtal_price && (
                                                                <p className="text-xs text-slate-400 line-through">
                                                                    Boxtal: {offer.boxtal_price?.toFixed(2)}€
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Config summary if enabled */}
                                                    {isEnabled && config && (
                                                        <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t border-slate-200">
                                                            <div>
                                                                <span className="text-slate-500">Franco</span>
                                                                <p className="font-medium">{config.franco || 500}€</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-slate-500">Cut-off</span>
                                                                <p className="font-medium">{config.cutoff || '17h'}</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-slate-500">Délai</span>
                                                                <p className="font-medium">{config.delay || '-'}</p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Edit button */}
                                                    {isEnabled && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="w-full mt-3"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openEditModal(offer);
                                                            }}
                                                        >
                                                            <Pencil className="w-4 h-4 mr-2" />
                                                            Configurer
                                                        </Button>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </TabsContent>

                    {/* Manual Carriers Tab */}
                    <TabsContent value="manual" className="mt-6">
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <p className="text-sm text-slate-600">
                                    Ajoutez des transporteurs manuels comme solution de secours si Boxtal n'est pas disponible.
                                </p>
                                <Button variant="outline" onClick={openManualModal}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Ajouter un transporteur
                                </Button>
                            </div>

                            {/* Manual carriers list */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {carriers
                                    .filter(c => !boxtalOffers.find(o => o.full_code === c.code))
                                    .map((carrier) => (
                                        <Card 
                                            key={carrier.code}
                                            className={`border-2 transition-all ${
                                                carrier.enabled 
                                                    ? 'border-green-200 bg-green-50/50' 
                                                    : 'border-slate-200 bg-slate-50 opacity-60'
                                            }`}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                            <Truck className="w-5 h-5 text-slate-400" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold text-slate-900">
                                                                {carrier.name}
                                                            </h3>
                                                            <p className="text-xs text-slate-500">{carrier.code}</p>
                                                        </div>
                                                    </div>
                                                    <p className="text-xl font-bold text-green-600">
                                                        {carrier.price?.toFixed(2) || '0.00'}€
                                                    </p>
                                                </div>

                                                <div className="grid grid-cols-4 gap-2 text-xs mb-3">
                                                    <div>
                                                        <span className="text-slate-500">Cut-off</span>
                                                        <p className="font-medium">{carrier.cutoff}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500">Délai</span>
                                                        <p className="font-medium">{carrier.delay || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500">Franco</span>
                                                        <p className="font-medium">{carrier.franco}€</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500">Samedi</span>
                                                        <p className="font-medium">{carrier.saturday_delivery ? 'Oui' : 'Non'}</p>
                                                    </div>
                                                </div>

                                                <div className="flex flex-wrap gap-1 mb-3">
                                                    {carrier.is_relay && (
                                                        <Badge variant="outline" className="text-xs">Relais</Badge>
                                                    )}
                                                    {carrier.is_pickup && (
                                                        <Badge variant="outline" className="text-xs">Retrait</Badge>
                                                    )}
                                                    <Badge variant="secondary" className="text-xs">Manuel</Badge>
                                                </div>

                                                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                                                    <div className="flex items-center gap-2">
                                                        <Switch
                                                            checked={carrier.enabled}
                                                            onCheckedChange={() => {
                                                                setCarriers(prev => prev.map(c => 
                                                                    c.code === carrier.code ? { ...c, enabled: !c.enabled } : c
                                                                ));
                                                            }}
                                                        />
                                                        <span className="text-sm text-slate-600">
                                                            {carrier.enabled ? 'Actif' : 'Inactif'}
                                                        </span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setEditingCarrier(carrier.code);
                                                                setCarrierForm({ ...carrier });
                                                                setModalOpen(true);
                                                            }}
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            onClick={() => {
                                                                setCarriers(prev => prev.filter(c => c.code !== carrier.code));
                                                                toast.success('Transporteur supprimé');
                                                            }}
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Edit Carrier Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Truck className="w-5 h-5 text-blue-600" />
                            Configurer le transporteur
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nom affiché</Label>
                            <Input
                                value={carrierForm.name}
                                onChange={(e) => setCarrierForm(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Euro className="w-4 h-4" />
                                    Prix (€)
                                </Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={carrierForm.price}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                />
                                {carrierForm.boxtal_price && (
                                    <p className="text-xs text-slate-500">
                                        Prix Boxtal: {carrierForm.boxtal_price?.toFixed(2)}€
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Euro className="w-4 h-4" />
                                    Franco (€)
                                </Label>
                                <Input
                                    type="number"
                                    value={carrierForm.franco}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, franco: parseFloat(e.target.value) || 0 }))}
                                />
                                <p className="text-xs text-slate-500">Seuil livraison gratuite</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    Cut-off
                                </Label>
                                <Input
                                    value={carrierForm.cutoff}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, cutoff: e.target.value }))}
                                    placeholder="17h"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Package className="w-4 h-4" />
                                    Délai de livraison
                                </Label>
                                <Input
                                    value={carrierForm.delay}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, delay: e.target.value }))}
                                    placeholder="J+1, 24h, 48h..."
                                />
                            </div>
                        </div>

                        <div className="space-y-3 pt-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label>Livraison le samedi</Label>
                                </div>
                                <Switch
                                    checked={carrierForm.saturday_delivery}
                                    onCheckedChange={(checked) => setCarrierForm(prev => ({ ...prev, saturday_delivery: checked }))}
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModalOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={saveCarrierConfig} className="bg-blue-600 hover:bg-blue-700">
                            <Save className="w-4 h-4 mr-2" />
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Manual Carrier Modal */}
            <Dialog open={manualModalOpen} onOpenChange={setManualModalOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5 text-green-600" />
                            Ajouter un transporteur manuel
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Code *</Label>
                                <Input
                                    value={carrierForm.code}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                                    placeholder="CHRP"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nom *</Label>
                                <Input
                                    value={carrierForm.name}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Chronopost"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Prix (€)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={carrierForm.price}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Franco (€)</Label>
                                <Input
                                    type="number"
                                    value={carrierForm.franco}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, franco: parseFloat(e.target.value) || 0 }))}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Cut-off</Label>
                                <Input
                                    value={carrierForm.cutoff}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, cutoff: e.target.value }))}
                                    placeholder="17h"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Délai</Label>
                                <Input
                                    value={carrierForm.delay}
                                    onChange={(e) => setCarrierForm(prev => ({ ...prev, delay: e.target.value }))}
                                    placeholder="J+1"
                                />
                            </div>
                        </div>

                        <div className="space-y-3 pt-2">
                            <div className="flex items-center justify-between">
                                <Label>Livraison le samedi</Label>
                                <Switch
                                    checked={carrierForm.saturday_delivery}
                                    onCheckedChange={(checked) => setCarrierForm(prev => ({ ...prev, saturday_delivery: checked }))}
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <Label>Point relais</Label>
                                <Switch
                                    checked={carrierForm.is_relay}
                                    onCheckedChange={(checked) => setCarrierForm(prev => ({ ...prev, is_relay: checked }))}
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <Label>Retrait sur place</Label>
                                <Switch
                                    checked={carrierForm.is_pickup}
                                    onCheckedChange={(checked) => setCarrierForm(prev => ({ ...prev, is_pickup: checked }))}
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setManualModalOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={saveManualCarrier} className="bg-green-600 hover:bg-green-700">
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default AdminShipping;
